/**
 * AddSpecModal — two-mode modal for adding a spec to a section.
 *
 * "Create new"     → creates a fresh ProgramSpec at the current scope
 * "Add existing"   → attaches an existing spec (from anywhere in the
 *                    cascade) to this section. The same spec row is used,
 *                    so any prior answers to it flow into this document
 *                    automatically.
 */
import { useEffect, useMemo, useState } from 'react'
import { Search } from 'lucide-react'
import Modal from '../ui/Modal'
import Badge from '../ui/Badge'
import { useUser } from '../../contexts/UserContext'
import {
  attachSpecToSection,
  createSpec,
  listVisibleSpecs,
} from './api'
import { CATEGORY_OPTIONS, OWNER_LEVEL_BADGE, SPEC_TYPE_OPTIONS } from './types'
import type { Spec, SpecCategory, SpecType, Scope } from './types'

interface Props {
  isOpen: boolean
  onClose: () => void
  sectionId: string
  scope: Scope
  nextOrder: number
  /** Spec IDs already attached to this section — excluded from the picker. */
  excludeSpecIds?: number[]
  onDone: () => Promise<void>
}

type Mode = 'chooser' | 'create_new' | 'add_existing'

function slugify(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '').slice(0, 80)
}

export default function AddSpecModal({
  isOpen,
  onClose,
  sectionId,
  scope,
  nextOrder,
  excludeSpecIds = [],
  onDone,
}: Props) {
  const { user } = useUser()
  const [mode, setMode] = useState<Mode>('chooser')

  // create-new state
  const [prompt, setPrompt] = useState('')
  const [key, setKey] = useState('')
  const [keyTouched, setKeyTouched] = useState(false)
  const [specType, setSpecType] = useState<SpecType>('text')
  const [category, setCategory] = useState<SpecCategory>('problem')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // add-existing state
  const [candidates, setCandidates] = useState<Spec[]>([])
  const [query, setQuery] = useState('')
  const [loadingCandidates, setLoadingCandidates] = useState(false)

  useEffect(() => {
    if (isOpen) {
      setMode('chooser')
      setPrompt('')
      setKey('')
      setKeyTouched(false)
      setSpecType('text')
      setCategory('problem')
      setQuery('')
      setError(null)
      setCandidates([])
    }
  }, [isOpen])

  useEffect(() => {
    if (!keyTouched) setKey(slugify(prompt))
  }, [prompt, keyTouched])

  useEffect(() => {
    if (mode !== 'add_existing' || !isOpen) return
    setLoadingCandidates(true)
    listVisibleSpecs({
      scope_level: scope.level,
      office_id: user?.office?.id || null,
      user_id: user?.id || null,
      program_id: scope.level === 'program' ? (scope.ownerId || null) : null,
    })
      .then(setCandidates)
      .catch((err) => setError(err.message || 'Failed to load specs'))
      .finally(() => setLoadingCandidates(false))
  }, [mode, isOpen, scope, user])

  const filteredCandidates = useMemo(() => {
    const excludeSet = new Set(excludeSpecIds)
    const needle = query.trim().toLowerCase()
    return candidates.filter((s) => {
      if (excludeSet.has(s.id)) return false
      if (!needle) return true
      return (
        s.key.toLowerCase().includes(needle) ||
        (s.prompt || '').toLowerCase().includes(needle) ||
        (s.guidance || '').toLowerCase().includes(needle)
      )
    })
  }, [candidates, query, excludeSpecIds])

  const handleCreateNew = async () => {
    if (!prompt.trim() || !key.trim()) {
      setError('Prompt and key are required')
      return
    }
    setSaving(true)
    setError(null)
    try {
      const spec = await createSpec({
        key: key.trim(),
        owner_level: scope.level,
        owner_office: scope.level === 'office' ? (scope.ownerId || null) : null,
        owner_user: scope.level === 'program_manager' ? (scope.ownerId || null) : null,
        owner_program: scope.level === 'program' ? (scope.ownerId || null) : null,
        parent: null,
        prompt: prompt.trim(),
        guidance: '',
        spec_type: specType,
        category,
        eligible_respondent_roles: ['pm'],
        interview_priority: 'standard',
      })
      await attachSpecToSection({
        section: sectionId,
        spec: spec.id,
        order: nextOrder,
        required: false,
      })
      await onDone()
      onClose()
    } catch (err: any) {
      setError(err.message || 'Failed to create spec')
    } finally {
      setSaving(false)
    }
  }

  const handleAddExisting = async (spec: Spec) => {
    setSaving(true)
    setError(null)
    try {
      await attachSpecToSection({
        section: sectionId,
        spec: spec.id,
        order: nextOrder,
        required: false,
      })
      await onDone()
      onClose()
    } catch (err: any) {
      setError(err.message || 'Failed to attach spec')
    } finally {
      setSaving(false)
    }
  }

  const footer = mode === 'create_new' ? (
    <>
      <button type="button" onClick={() => setMode('chooser')} className="px-4 py-2 text-sm border border-border rounded-md" disabled={saving}>
        Back
      </button>
      <button type="button" onClick={handleCreateNew} disabled={saving || !prompt.trim() || !key.trim()} className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-md disabled:opacity-50">
        {saving ? 'Creating…' : 'Create & attach'}
      </button>
    </>
  ) : mode === 'add_existing' ? (
    <button type="button" onClick={() => setMode('chooser')} className="px-4 py-2 text-sm border border-border rounded-md" disabled={saving}>
      Back
    </button>
  ) : (
    <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-border rounded-md">
      Cancel
    </button>
  )

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add spec" size="lg" footer={footer}>
      {mode === 'chooser' && (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Add a spec to this section. You can write a new one or pick from specs already in use elsewhere —
            reusing an existing spec maps any prior answers to it through to this document.
          </p>
          <div className="grid grid-cols-2 gap-3 pt-2">
            <button
              type="button"
              onClick={() => setMode('create_new')}
              className="bg-card border border-border rounded-lg p-4 text-left hover:border-primary/60"
            >
              <div className="font-medium text-foreground mb-1">Create new</div>
              <div className="text-xs text-muted-foreground">
                Write a fresh spec at the {scope.level.replace('_', ' ')} scope.
              </div>
            </button>
            <button
              type="button"
              onClick={() => setMode('add_existing')}
              className="bg-card border border-border rounded-lg p-4 text-left hover:border-primary/60"
            >
              <div className="font-medium text-foreground mb-1">Add existing</div>
              <div className="text-xs text-muted-foreground">
                Pick from specs already used at system, office, or your scope. Answers carry through.
              </div>
            </button>
          </div>
        </div>
      )}

      {mode === 'create_new' && (
        <div className="space-y-3">
          <label className="block">
            <span className="text-sm font-medium">Prompt</span>
            <textarea
              autoFocus
              rows={2}
              className="mt-1 w-full px-3 py-2 rounded-md border border-input bg-background text-foreground"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="What is the operational environment?"
            />
          </label>
          <label className="block">
            <span className="text-sm font-medium">Key</span>
            <input
              type="text"
              className="mt-1 w-full px-3 py-2 rounded-md border border-input bg-background text-foreground font-mono text-sm"
              value={key}
              onChange={(e) => { setKey(e.target.value); setKeyTouched(true) }}
              placeholder="operational_environment"
            />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-sm font-medium">Answer type</span>
              <select
                className="mt-1 w-full px-3 py-2 rounded-md border border-input bg-background text-foreground"
                value={specType}
                onChange={(e) => setSpecType(e.target.value as SpecType)}
              >
                {SPEC_TYPE_OPTIONS.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-sm font-medium">Category</span>
              <select
                className="mt-1 w-full px-3 py-2 rounded-md border border-input bg-background text-foreground"
                value={category}
                onChange={(e) => setCategory(e.target.value as SpecCategory)}
              >
                {CATEGORY_OPTIONS.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </label>
          </div>
          <p className="text-xs text-muted-foreground">
            Units and options (for categorical types) can be set after creating, in the spec editor.
          </p>
          {error && <div className="text-sm text-destructive">{error}</div>}
        </div>
      )}

      {mode === 'add_existing' && (
        <div className="space-y-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <input
              autoFocus
              type="text"
              className="w-full pl-9 pr-3 py-2 rounded-md border border-input bg-background text-foreground"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by prompt, key, or guidance…"
            />
          </div>
          {loadingCandidates ? (
            <div className="text-sm text-muted-foreground">Loading…</div>
          ) : filteredCandidates.length === 0 ? (
            <div className="text-sm text-muted-foreground italic">
              {candidates.length === 0 ? 'No specs exist at this scope yet.' : 'No matches.'}
            </div>
          ) : (
            <ul className="space-y-2 max-h-96 overflow-auto">
              {filteredCandidates.map((s) => {
                const badge = OWNER_LEVEL_BADGE[s.owner_level]
                return (
                  <li key={s.id}>
                    <button
                      type="button"
                      onClick={() => handleAddExisting(s)}
                      disabled={saving}
                      className="w-full text-left bg-card border border-border hover:border-primary/60 rounded-lg p-3 disabled:opacity-50"
                    >
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-xs text-muted-foreground">{s.key}</span>
                        <Badge variant={badge.variant}>{badge.label}</Badge>
                        {s.interview_priority && <Badge variant="info">{s.interview_priority}</Badge>}
                      </div>
                      <div className="mt-1 text-sm text-foreground">
                        {s.prompt || <span className="italic text-muted-foreground">No prompt</span>}
                      </div>
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
          {error && <div className="text-sm text-destructive">{error}</div>}
        </div>
      )}
    </Modal>
  )
}
